import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Divider,
  Button
} from '@mui/material';
import {
  Security,
  Lock,
  Person,
  ContactSupport,
  ArrowBack,
  Storage,
  CalendarMonth,
  Watch
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { usePageSeo } from './usePageSeo';

const TimeDrawPrivacyPolicy = () => {
  const navigate = useNavigate();
  usePageSeo({
    title: 'Privacy Policy | TimeDraw',
    description: 'Privacy policy for TimeDraw, an iOS and Apple Watch app that visualizes calendar events on an analog clockface by Michael R. Ellis.',
    path: '/timedraw-privacy-policy'
  });

  return (
    <>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')}
          sx={{ mb: 3 }}
        >
          Back to Portfolio
        </Button>

        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Privacy Policy
          </Typography>

          <Typography variant="body2" color="text.secondary" gutterBottom>
            Effective Date: September 27, 2026
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            App Name: TimeDraw
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Platform: iOS, iPadOS, and watchOS
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Developer: Michael R. Ellis
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Security sx={{ mr: 1 }} />
              Overview
            </Typography>
            <Typography variant="body1" paragraph>
              TimeDraw helps you visualize calendar events and reminders on an analog clockface on iPhone, iPad, and Apple Watch. Your schedule stays in Apple&apos;s Calendar and Reminders systems; TimeDraw reads and displays that information locally and can create items you add in the app. We do not operate user accounts, we do not collect personal information on our own servers, and we do not sell or share data for advertising.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <CalendarMonth sx={{ mr: 1 }} />
              Calendar &amp; Reminders Access
            </Typography>
            <Typography variant="body1" paragraph>
              With your permission, TimeDraw uses Apple&apos;s EventKit framework to read your calendars, events, and reminders so it can map them onto the clockface, and to save new events or reminders you create in the app. iOS or watchOS asks for this permission when it is needed, and you can change or revoke it at any time in Settings under Privacy &amp; Security.
            </Typography>
            <Typography variant="body1" paragraph>
              Calendar and reminder content is processed on your device for display and editing. TimeDraw does not copy your events or reminders to any server we operate, and it does not transmit their titles, times, locations, attendees, or notes to us. If your calendars sync with iCloud, Google, Exchange, or another provider, that syncing is handled by Apple and that provider under their own privacy policies, not by TimeDraw.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Watch sx={{ mr: 1 }} />
              Apple Watch
            </Typography>
            <Typography variant="body1" paragraph>
              The Apple Watch companion shows the same clockface-style view using calendar data available to the watch. Data shown on the watch stays within Apple&apos;s watch and phone ecosystem according to your sync and permission settings. We do not receive watch usage or calendar content on our servers.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Storage sx={{ mr: 1 }} />
              Information Stored on Your Device
            </Typography>
            <Typography variant="body1" paragraph>
              To provide core features, TimeDraw may store preferences locally on your device, such as:
            </Typography>
            <Typography component="ul" variant="body1" sx={{ pl: 3 }}>
              <li>Which calendars you choose to show or hide</li>
              <li>Display and layout preferences for the clockface</li>
              <li>Settings related to creating events and reminders in the app</li>
              <li>Onboarding or interface state needed for the app to work as you left it</li>
            </Typography>
            <Typography variant="body1" paragraph sx={{ mt: 2 }}>
              These preferences stay on your device. Removing the app deletes the data TimeDraw stored locally, subject to how iOS and watchOS manage app deletion.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Lock sx={{ mr: 1 }} />
              Data Collection
            </Typography>
            <Typography variant="body1" paragraph>
              We do not collect data from this app for our own purposes. There is no account registration or login with us, so we do not collect your name, email address, or payment details through TimeDraw. The app is not designed to send your calendar content or personal information to developer-operated servers. We do not use advertising networks or cross-app tracking in TimeDraw.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Person sx={{ mr: 1 }} />
              Third-Party Links
            </Typography>
            <Typography variant="body1" paragraph>
              The app may link to third-party pages, such as the App Store listing or the developer website. If you open those links, the privacy practices of those sites apply, not this policy.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom>
              Children&apos;s Privacy
            </Typography>
            <Typography variant="body1" paragraph>
              TimeDraw is not directed at children under 13 and does not knowingly collect personal information from children. No personal data is required to use its features.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom>
              Your Choices &amp; Rights
            </Typography>
            <Typography variant="body1" paragraph>
              You control calendar and reminders access in Settings and can revoke it at any time. You can change preferences inside the app or remove the app to delete local data it stored. Because we do not operate accounts or store your calendar data on our servers, there is no cloud profile for us to access, export, or delete on your behalf.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom>
              Changes to This Policy
            </Typography>
            <Typography variant="body1" paragraph>
              We may update this Privacy Policy from time to time. The effective date at the top of this page will be revised when changes are posted. Continued use of the app after an update constitutes acceptance of the revised policy.
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <ContactSupport sx={{ mr: 1 }} />
              Contact
            </Typography>
            <Typography variant="body1" paragraph>
              For privacy questions about TimeDraw, contact:{' '}
              <a href="mailto:Contact@MichaelRobertEllis.com" style={{ color: '#1976d2', textDecoration: 'none' }}>
                Contact@MichaelRobertEllis.com
              </a>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </>
  );
};

export default TimeDrawPrivacyPolicy;
